import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

/**
 * <bouquet-contents> — the bouquet PDP "what's inside" block.
 *
 * On a variant change (Standard <-> Premium) the theme's default behaviour morphs
 * only the <variant-picker>; every other piece self-updates by listening to the
 * ProductSelectEvent and morphing itself from the fetched product HTML (see
 * product-price.js / product-inventory.js). This component does the same for the
 * flower-tile grid, the stem-count subhead and the savings panel — all of which are
 * server-rendered per the selected variant — and additionally owns the flower-preview
 * <dialog> and the mobile "See all N flowers" collapse.
 *
 * The interactivity lives on the persistent host element (not an inline script inside
 * the morphed subtree) so it survives a morph: the host is never replaced by
 * childrenOnly morph, and the dialog/peek are matched by their stable ids and patched
 * in place, so delegated listeners keep working and no re-initialisation is needed.
 */
class BouquetContents extends Component {
  /** @type {Element | null} */
  #section = null;

  /** @type {HTMLElement | null} */
  #lastFocus = null;

  /** @type {number | undefined} */
  #resizeTimer;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#onClick);
    this.addEventListener('focusin', this.#onFocusIn);
    window.addEventListener('resize', this.#onResize);
    window.addEventListener('load', this.#tuneClip);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.#tuneClip);

    this.#dialog?.addEventListener('close', this.#onDialogClose);
    this.#tuneClip();

    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#resizeTimer);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('focusin', this.#onFocusIn);
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('load', this.#tuneClip);
    document.removeEventListener('keydown', this.#onKeydown);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  /** @returns {HTMLDialogElement | null} */
  get #dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('.tk-bq__pop'));
  }

  /** @returns {HTMLElement | null} */
  get #peek() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.tk-bq__peek'));
  }

  /** @returns {HTMLElement | null} */
  get #toggle() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.tk-bq__toggle'));
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!(target instanceof Element)) return;

    const tile = target.closest('.tk-bq__tile');
    if (tile && this.contains(tile)) {
      this.#openPreview(/** @type {HTMLElement} */ (tile));
      return;
    }

    const toggle = target.closest('.tk-bq__toggle');
    if (toggle && this.contains(toggle)) {
      this.#setOpen(!this.#peek?.classList.contains('is-open'));
      return;
    }

    const dialog = this.#dialog;
    if (dialog && (target === dialog || target.closest('[data-close]'))) {
      this.#closePreview();
    }
  };

  /** @param {Event} event */
  #onFocusIn = (event) => {
    const peek = this.#peek;
    if (!peek || !peek.classList.contains('tk-bq__peek--collapsible') || peek.classList.contains('is-open')) return;

    const target = /** @type {Element} */ (event.target);
    if (target instanceof Element && target.closest('.tk-bq__tile') && window.matchMedia('(max-width: 749px)').matches) {
      this.#setOpen(true);
    }
  };

  #onResize = () => {
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = window.setTimeout(this.#tuneClip, 150);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape') this.#closePreview();
  };

  #onDialogClose = () => {
    document.removeEventListener('keydown', this.#onKeydown);
  };

  /** @param {HTMLElement} tile */
  #openPreview(tile) {
    const dialog = this.#dialog;
    if (!dialog) return;

    const data = tile.dataset;
    const img = /** @type {HTMLImageElement | null} */ (dialog.querySelector('.tk-bq__pop-img'));
    const name = dialog.querySelector('.tk-bq__pop-name');
    const meta = dialog.querySelector('.tk-bq__pop-meta');
    const price = dialog.querySelector('.tk-bq__pop-price');
    const link = /** @type {HTMLAnchorElement | null} */ (dialog.querySelector('.tk-bq__pop-link'));

    this.#lastFocus = tile;

    if (img) {
      if (data.img) {
        img.classList.add('is-loading');
        img.removeAttribute('src');
        img.alt = (data.name || '') + (data.variant ? ', ' + data.variant : '');
        img.onload = () => img.classList.remove('is-loading');
        img.src = data.img;
        if (img.complete && img.naturalWidth) img.classList.remove('is-loading');
        img.style.display = '';
      } else {
        img.onload = null;
        img.removeAttribute('src');
        img.style.display = 'none';
      }
    }

    if (name) name.textContent = data.name || '';

    if (meta) {
      let m = data.variant || '';
      if (data.qty && data.qty !== '1') m += (m ? '   ·   ' : '') + '×' + data.qty + ' in this bouquet';
      meta.textContent = m;
    }

    if (price) price.textContent = 'Sold on its own for ' + (data.price || '');
    if (link) link.href = data.url || '#';

    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    document.addEventListener('keydown', this.#onKeydown);
  }

  #closePreview() {
    const dialog = this.#dialog;
    const wasOpen = !!dialog && (dialog.open || dialog.hasAttribute('open'));
    document.removeEventListener('keydown', this.#onKeydown);
    if (!wasOpen) return;

    if (typeof dialog.close === 'function') {
      try {
        dialog.close();
      } catch (e) {
        dialog.removeAttribute('open');
      }
    } else {
      dialog.removeAttribute('open');
    }

    this.#lastFocus?.focus();
    this.#lastFocus = null;
  }

  /** @param {boolean} open */
  #setOpen(open) {
    const peek = this.#peek;
    if (!peek) return;

    peek.classList.toggle('is-open', open);

    const toggle = this.#toggle;
    if (toggle) {
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      const txt = toggle.querySelector('.tk-bq__toggle-txt');
      if (txt) {
        txt.textContent = open
          ? toggle.dataset.lessLabel || 'Show fewer'
          : toggle.dataset.moreLabel || txt.textContent;
      }
    }

    this.#tuneClip();
  }

  #tuneClip = () => {
    const peek = this.#peek;
    if (!peek || !peek.classList.contains('tk-bq__peek--collapsible')) return;

    const scrim = /** @type {HTMLElement | null} */ (peek.querySelector('.tk-bq__scrim'));
    const mobile = window.matchMedia('(max-width: 749px)').matches;

    if (!mobile || peek.classList.contains('is-open')) {
      peek.style.maxHeight = '';
      if (scrim) scrim.style.height = '';
      return;
    }

    const first = peek.querySelector('.tk-bq__tile');
    if (!first) return;

    const h = first.getBoundingClientRect().height;
    if (!h) return;

    peek.style.maxHeight = Math.round(h * 1.5 + 10) + 'px';
    if (scrim) scrim.style.height = Math.round(h * 0.5) + 'px';
  };

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;
        if (newProduct) this.dataset.productId = newProduct.id;
        else if (detail.productId && detail.productId !== this.dataset.productId) return;

        const counterpart = html.querySelector(`bouquet-contents[data-block-id="${this.dataset.blockId}"]`);
        if (!counterpart) return;

        const wasOpen = this.#peek?.classList.contains('is-open') ?? false;
        this.#closePreview();

        morph(this, counterpart, { ...MORPH_OPTIONS, childrenOnly: true });

        // The dialog node is matched by its stable id and patched in place, but re-bind
        // defensively in case morph ever replaces it (addEventListener dedupes identical pairs).
        this.#dialog?.addEventListener('close', this.#onDialogClose);

        if (wasOpen) this.#setOpen(true);
        else this.#tuneClip();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bouquet-contents] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('bouquet-contents')) {
  customElements.define('bouquet-contents', BouquetContents);
}

/**
 * <bouquet-value-pill> — the compact savings pill shown right after the buy buttons.
 * Purely presentational: it self-morphs from the fetched product HTML on a variant
 * change so its "you save about $X" figure tracks the selected variant.
 */
class BouquetValuePill extends Component {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;
        if (newProduct) this.dataset.productId = newProduct.id;
        else if (detail.productId && detail.productId !== this.dataset.productId) return;

        const counterpart = html.querySelector(`bouquet-value-pill[data-block-id="${this.dataset.blockId}"]`);
        if (!counterpart) return;

        morph(this, counterpart, { ...MORPH_OPTIONS, childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bouquet-value-pill] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('bouquet-value-pill')) {
  customElements.define('bouquet-value-pill', BouquetValuePill);
}
